import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight, CheckCircle, Zap, Star } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { getWhatsAppLink } from "@/lib/whatsapp";

interface ContentSection {
  title: string;
  content: string;
  items?: string[];
  grid?: { icon: string; title: string; description: string }[];
}

interface FAQ {
  question: string;
  answer: string;
}

interface RelatedPage {
  href: string;
  label: string;
}

interface SEOLandingPageProps {
  lang?: "en" | "de";
  title: string;
  metaDescription: string;
  keywords: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  hreflang?: { lang: string; href: string }[];
  heroTitle: string;
  heroHighlight: string;
  heroDescription: string;
  sections: ContentSection[];
  faqs: FAQ[];
  relatedPages: RelatedPage[];
  schemaType?: string;
  schemaData?: Record<string, unknown>;
}

const SEOLandingPage = ({
  lang = "en",
  title,
  metaDescription,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  hreflang = [],
  heroTitle,
  heroHighlight,
  heroDescription,
  sections,
  faqs,
  relatedPages,
  schemaType = "Service",
  schemaData = {},
}: SEOLandingPageProps) => {
  const whatsappLink = getWhatsAppLink(
    lang === "de"
      ? "Hallo! Ich möchte ein kostenloses Beispielvideo für mein Business 🚀"
      : undefined
  );

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: title.split("|")[0]?.trim(),
    description: metaDescription,
    provider: { "@type": "Organization", name: "Fuel72", url: "https://www.fuel72.com" },
    areaServed: ["US", "CA", "AU", "GB", "DE", "AT", "CH"],
    ...schemaData,
  };

  const ctaText = lang === "de" ? "Kostenloses Beispiel anfordern" : "Get Free Sample Video";
  const ctaTextShort = lang === "de" ? "Jetzt starten" : "Get Started";

  return (
    <>
      <Helmet>
        {lang === "de" && <html lang="de" />}
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonical} />
        {hreflang.map((h) => (
          <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.href} />
        ))}
        <meta property="og:title" content={ogTitle || title} />
        <meta property="og:description" content={ogDescription || metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.fuel72.com/og-image.jpg" />
        {lang === "de" && <meta property="og:locale" content="de_DE" />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle || title} />
        <meta name="twitter:description" content={ogDescription || metaDescription} />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen pt-20">
        {/* Hero */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold mb-6 leading-tight">
              {heroTitle}{" "}
              <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent">
                {heroHighlight}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              {heroDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold text-lg transition-all hover:scale-105 shadow-lg shadow-green-500/25"
              >
                <MessageCircle className="w-5 h-5" />
                {ctaText}
              </a>
              <Link
                to="/portfolio"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl border border-border hover:border-orange-500/50 text-foreground font-semibold transition-all hover:bg-orange-500/5"
              >
                {lang === "de" ? "Portfolio ansehen" : "View Portfolio"}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-6 mt-12 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-orange-400" /> {lang === "de" ? "72h Lieferung" : "72h Delivery"}</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-orange-400" /> 4.9/5 {lang === "de" ? "Bewertung" : "Rating"}</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-orange-400" /> {lang === "de" ? "Keine Influencer nötig" : "No Influencers Needed"}</span>
            </div>
          </div>
        </section>

        {/* Content Sections */}
        {sections.map((section, i) => (
          <section
            key={i}
            className={`py-16 md:py-20 ${i % 2 === 0 ? "" : "bg-card/30"}`}
          >
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">{section.title}</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{section.content}</p>

              {section.items && (
                <ul className="space-y-3">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.grid && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                  {section.grid.map((card, j) => (
                    <div
                      key={j}
                      className="p-5 rounded-xl border border-border/50 bg-card/50 hover:border-orange-500/30 transition-colors"
                    >
                      <span className="text-2xl mb-2 block">{card.icon}</span>
                      <h3 className="font-semibold mb-1">{card.title}</h3>
                      <p className="text-sm text-muted-foreground">{card.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Mid-section CTA every other section */}
              {i === 1 && (
                <div className="mt-10 text-center">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold transition-all hover:scale-105"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {ctaText}
                  </a>
                </div>
              )}
            </div>
          </section>
        ))}

        {/* FAQ Section */}
        {faqs.length > 0 && (
          <section className="py-16 md:py-20">
            <div className="container mx-auto px-4 max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                {lang === "de" ? "Häufig gestellte Fragen" : "Frequently Asked Questions"}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border border-border/50 rounded-xl px-4">
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {/* Internal Links */}
        {relatedPages.length > 0 && (
          <section className="py-12 bg-card/30">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-xl font-semibold mb-6 text-center">
                {lang === "de" ? "Weitere Services" : "Explore More"}
              </h2>
              <div className="flex flex-wrap justify-center gap-3">
                {relatedPages.map((page, i) => (
                  <Link
                    key={i}
                    to={page.href}
                    className="px-4 py-2 rounded-lg border border-border/50 text-sm hover:border-orange-500/50 hover:text-orange-400 transition-colors"
                  >
                    {page.label}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="py-20 md:py-28 text-center">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {lang === "de" ? "Bereit durchzustarten?" : "Ready to Get Started?"}
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              {lang === "de"
                ? "Hol dir jetzt dein kostenloses Beispielvideo und überzeuge dich selbst."
                : "Get your free sample video today and see the quality for yourself."}
            </p>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-10 py-5 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-lg transition-all hover:scale-105 shadow-xl shadow-orange-500/25"
            >
              <MessageCircle className="w-5 h-5" />
              {ctaText}
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default SEOLandingPage;
